import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export default function Login() {
  const { login, loginWithMicrosoft, error: authError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const localMode = params.get("local") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { if (authError) setErr(authError); }, [authError]);
  const [loading, setLoading] = useState(false);
  const [sso, setSso] = useState(null);
  const [ssoBusy, setSsoBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSso = async (attempt = 0) => {
      try {
        const r = await api.get("/auth/sso-config");
        if (!cancelled) setSso(r.data);
      } catch {
        if (!cancelled && attempt < 5) setTimeout(() => fetchSso(attempt + 1), 1500 * (attempt + 1));
        else if (!cancelled) setSso({ enabled: false, unreachable: true });
      }
    };
    fetchSso();
    return () => { cancelled = true; };
  }, []);

  const goHome = (u) => navigate(u?.role === "kiosk" ? "/board" : "/");

  const microsoft = async () => {
    setErr("");
    setSsoBusy(true);
    try {
      const u = await loginWithMicrosoft();
      toast.success("Signed in with Microsoft");
      goHome(u);
    } catch (e) {
      if (!/user_cancelled/i.test(e.errorCode || e.message)) setErr(e.message);
    } finally {
      setSsoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Signed in");
      goHome(u);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#f7f5f1]">
      {/* Left - Brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-[#e6e5df] relative overflow-hidden grid-bg bg-[#efece5]">
        <div className="flex items-center gap-3">
          <img
            src="/misco-logo.webp"
            alt="MISCO"
            data-testid="brand-logo-login"
            className="h-12 w-auto"
          />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#647265] mt-1.5 font-mono border-l border-[#cfcfc9] pl-3">Rework Hub</div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a39f98] font-mono mb-4">// Central Register</div>
          <div className="grid grid-cols-3 gap-4 mt-2 pt-6 border-t border-[#cfcfc9]">
            {[
              { k: "1", v: "Central Register" },
              { k: "8", v: "Stages" },
              { k: "SSO", v: "Microsoft Sign-in" },
            ].map((s) => (
              <div key={s.v}>
                <div className="num text-3xl text-[#424e43]">{s.k}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6b6a68] mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[10px] font-mono text-[#a39f98] uppercase tracking-widest">Production • Quality • Accountability</div>
      </div>

      {/* Right - Form */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-[#f7f5f1]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-6">
            <img
              src="/misco-logo.webp"
              alt="MISCO"
              className="h-10 w-auto"
            />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#647265] font-mono border-l border-[#cfcfc9] pl-3">Rework Hub</div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a39f98] font-mono mb-2">Sign in</div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight text-[#232826]">Welcome back.</h1>
            <p className="text-sm text-[#6b6a68] mt-1.5">
              {localMode ? "Local admin fallback sign-in." : "Sign in with your MISCO Microsoft work account."}
            </p>
          </div>

          {localMode && (
            <>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#6b6a68]">Email</Label>
                <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-[#cfcfc9] rounded-sm focus-visible:ring-[#647265] text-[#232826]" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#6b6a68]">Password</Label>
                <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-white border-[#cfcfc9] rounded-sm focus-visible:ring-[#647265] text-[#232826]" />
              </div>
            </>
          )}

          {err && (
            <div className="text-sm text-[#8a3a2a] border border-[#8a3a2a]/30 bg-[#8a3a2a]/10 px-3 py-2 rounded-sm" data-testid="login-error">
              {err}
            </div>
          )}

          {localMode && (
            <Button type="submit" disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm font-semibold h-11 tracking-wide uppercase text-xs">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          )}

          {sso === null ? (
            <div className="h-12 rounded-sm border border-[#e6e5df] bg-[#efece5]/60 flex items-center justify-center gap-2 text-xs text-[#6b6a68]" data-testid="login-microsoft-loading">
              <MicrosoftLogo /> Checking Microsoft sign-in…
            </div>
          ) : sso.unreachable ? (
            <Button type="button" onClick={() => window.location.reload()} variant="outline" data-testid="login-microsoft-retry"
              className="w-full rounded-sm h-12 border-[#cfcfc9] bg-white hover:bg-[#efece5] text-[#232826] text-xs uppercase tracking-wide font-semibold">
              <MicrosoftLogo /><span className="ml-2">Can't reach the server — tap to retry</span>
            </Button>
          ) : sso.enabled ? (
            <Button type="button" onClick={microsoft} disabled={ssoBusy} variant={localMode ? "outline" : "default"} data-testid="login-microsoft"
              className={`w-full rounded-sm h-12 text-xs uppercase tracking-wide font-semibold ${localMode
                ? "border-[#cfcfc9] bg-white hover:bg-[#efece5] text-[#232826]"
                : "bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1]"}`}>
              <MicrosoftLogo /><span className="ml-2">{ssoBusy ? "Opening Microsoft…" : "Sign in with Microsoft"}</span>
            </Button>
          ) : (
            <div className="text-[11px] text-[#a39f98] flex items-center gap-2" data-testid="login-microsoft-disabled"><MicrosoftLogo /> Microsoft sign-in available once Entra IDs are configured.</div>
          )}

          <p className="text-xs text-[#6b6a68] leading-relaxed" data-testid="login-invite-note">
            Access is invite-only. If your Microsoft account isn't recognised, ask a Rework Hub admin to add you.
          </p>

          <div className="pt-2 text-xs text-[#a39f98] flex items-center justify-end">
            <span className="font-mono">v1.1</span>
          </div>
        </form>
      </div>
    </div>
  );
}
